import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import dayjs from "dayjs";
import { removeUnderScoreWithUpperCase } from "../../../utils/removeUnderscore";
import { useGetAllSuppliers, useGetGrazeons } from "../queryHooks";
import { useOrdersStore } from "../store/useOrdersStore";

export type OrderTimelineEntry = {
  scope?: string;
  from?: string | null;
  to?: string;
  userId?: string;
  supplierId?: string;
  at?: string | Date;
};

type AuditTrailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderTimeline?: OrderTimelineEntry[] | null;
};

const formatStatus = (status?: string | null) => {
  if (!status) return null;
  const leaf = status.includes(".") ? status.split(".").pop() : status;
  return removeUnderScoreWithUpperCase(leaf ?? status);
};

/** State that was left at this event (`from`); fall back to `to` for the first entry. */
const formatLeavingState = (entry: OrderTimelineEntry) => {
  return formatStatus(entry.from) ?? formatStatus(entry.to) ?? "-";
};

const formatCompactTime = (at?: string | Date) => {
  if (!at) return "-";
  const parsed = dayjs(at);
  return parsed.isValid() ? parsed.format("DD MMM, hh:mm A") : "-";
};

const idKey = (id?: string | null) => (id ? String(id) : "");

const AuditTrailModal = ({
  isOpen,
  onClose,
  orderTimeline,
}: AuditTrailModalProps) => {
  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: grazeons } = useGetGrazeons();
  const orderHandlers = useOrdersStore((state) => state.orderHandlers);

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};

    orderHandlers?.forEach((handler: any) => {
      const user = handler?.user;
      const key = idKey(user?._id || handler?.userId || handler?._id);
      if (!key) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    const grazeonList = Array.isArray(grazeons?.data)
      ? grazeons.data
      : Array.isArray(grazeons)
        ? grazeons
        : [];
    grazeonList.forEach((grazeon: any) => {
      const user = grazeon?.user;
      const key = idKey(user?._id || grazeon?.userId);
      if (!key || map[key]) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    return map;
  }, [orderHandlers, grazeons]);

  const supplierNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (allSuppliers || []).forEach((supplier: any) => {
      const key = idKey(supplier?._id);
      if (key && supplier?.businessName) {
        map[key] = supplier.businessName;
      }
    });
    return map;
  }, [allSuppliers]);

  const resolvePerformedBy = (entry: OrderTimelineEntry) => {
    const userName = userNameById[idKey(entry.userId)];
    const supplierName = supplierNameById[idKey(entry.supplierId)];

    if (userName && supplierName) {
      return `${userName} · ${supplierName}`;
    }
    if (userName) return userName;
    if (supplierName) return supplierName;
    if (entry.userId || entry.supplierId) return "Unknown";
    return "System";
  };

  const entries = useMemo(() => {
    if (!Array.isArray(orderTimeline)) return [];
    // Oldest first — event that occurred first appears first.
    return [...orderTimeline].sort((a, b) => {
      const aTime = a?.at ? new Date(a.at).getTime() : 0;
      const bTime = b?.at ? new Date(b.at).getTime() : 0;
      return aTime - bTime;
    });
  }, [orderTimeline]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      scrollBehavior="inside"
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader py={3} fontSize="md">
          Audit Trail
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody py={2}>
          {entries.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              No audit trail events available.
            </Text>
          ) : (
            <VStack align="stretch" spacing={1.5}>
              {entries.map((entry, idx) => {
                const performedBy = resolvePerformedBy(entry);
                const leavingState = formatLeavingState(entry);
                return (
                  <Box
                    key={`${entry.scope}-${entry.from}-${entry.to}-${entry.at}-${idx}`}
                  >
                    <Text fontSize="sm" whiteSpace="normal" wordBreak="break-word">
                      <Text as="span" fontWeight="semibold">
                        {performedBy}:
                      </Text>
                      <Text
                        as="span"
                        ml={1}
                        color={
                          performedBy === "System" ? "red.500" : "gray.800"
                        }
                      >
                        {leavingState}
                      </Text>
                      <Text as="span" ml={1} color="gray.500">
                        [{formatCompactTime(entry.at)}]
                      </Text>
                    </Text>
                  </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter py={2}>
          <Button colorScheme="blue" size="sm" onClick={onClose}>
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuditTrailModal;
